import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  Copy,
  LogOut,
  Building2,
  ShieldCheck,
  Phone,
  Crown,
  Lock,
} from 'lucide-react';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useLogout } from '../../application/hooks/useAuth';
import { useTranslation } from '../../application/hooks/useTranslation';
import { QueryState } from '../components/feedback/QueryState';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TierLevel, TIER_DESIGNATION_MAP } from '../../domain/enums/TierLevel';
import { KycDetailsCard } from '../components/profile/KycDetailsCard';
import { NomineeCard } from '../components/profile/NomineeCard';
import { LinkedPaymentCard } from '../components/profile/LinkedPaymentCard';
import { ChangePasswordCard } from '../components/profile/ChangePasswordCard';
import { initials as nameInitials } from './team/teamFormat';
import './pages.css';
import './Profile.css';

/**
 * World-Class Owner Profile View (W04).
 * Combines exclusive black & gold owner card with bank-grade KYC status and succession details.
 */
export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isPending, error, refetch } = useCurrentUser();
  const logout = useLogout();
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(user.apartmentId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — id stays visible on screen */
    }
  };

  const initials = user?.fullName?.trim() ? nameInitials(user.fullName) : 'SG';

  const isL1 = user?.tier === TierLevel.L1;
  const tierTitle = user ? TIER_DESIGNATION_MAP[user.tier] ?? user.tier : 'Owner';
  // PRD W04 §4.2: L1–L5 without a sponsor get an advisory; Directors (L6) have none by design.
  const needsSponsor = !!user && user.tier !== TierLevel.L6 && user.tier !== TierLevel.ADMIN && !user.parentId;

  return (
    <div data-testid="profile-page">
      <div className="page-head">
        <h1 className="page-title">{t('profile.title')}</h1>
        <Badge tone="neutral">{t('profile.readOnly')}</Badge>
      </div>

      <QueryState loading={isPending} error={error} onRetry={() => void refetch()} skeletonCount={2} />

      {user && (
        <div className="stack">
          {/* 1. Owner Profile Card (Black & Gold / Emerald Luxury Card) */}
          <div className="profile-passport-card">
            <div className="profile-passport-inner">
              <div className="profile-passport-top">
                <div className="profile-passport-brand">
                  <span className="profile-passport-kicker">
                    <Building2 size={13} color="#fed766" /> Sristi Green Valley Ltd
                  </span>
                  <span className="profile-passport-org">{isL1 ? t('profile.passportL1') : t('profile.passportAgent')}</span>
                </div>

                <div className="profile-passport-tier-pill">
                  <Crown size={14} color="#fed766" />
                  <span>{tierTitle}</span>
                </div>
              </div>

              <div className="profile-passport-middle">
                <div className="profile-user-group">
                  <div className="profile-avatar-circle">{initials}</div>
                  <div className="profile-user-titles">
                    <h2 className="profile-user-name">{user.fullName}</h2>
                    <span className="profile-user-phone">
                      <Phone size={13} /> {user.phone}
                    </span>
                  </div>
                </div>

                <div className="profile-account-id-box">
                  <span className="profile-account-id-label">{isL1 ? t('profile.accountIdLabelL1') : t('profile.accountIdLabelAgent')}</span>
                  <div className="profile-id-chip">
                    <span>{user.apartmentId}</span>
                    <button
                      className="profile-copy-btn"
                      onClick={() => void copyId()}
                      aria-label={t('profile.copyId')}
                      data-testid="copy-id"
                      title="Copy Account ID"
                    >
                      {copied ? <Check size={16} color="#10b981" /> : <Copy size={16} />}
                    </button>
                  </div>
                  {copied && (
                    <span role="status" style={{ fontSize: '0.6875rem', color: '#a7f3d0', fontWeight: 600 }}>
                      ✓ {t('profile.copied')}
                    </span>
                  )}
                </div>
              </div>

              <div className="profile-passport-bottom">
                <div className="profile-security-badge">
                  <ShieldCheck size={15} color="#10b981" />
                  <span>Central Corporate Escrow Registry</span>
                </div>
                {isL1 && <span style={{ opacity: 0.85 }}>SGVL Residential Tenure</span>}
              </div>
            </div>
          </div>

          {/* 2. Structured 2-Column Detail Cards */}
          <div className="profile-detail-grid">
            {/* Primary Owner Details */}
            <Card title={user.fullName}>
              <dl className="kv">
                <dt>{t('profile.accountId')}</dt>
                <dd className="tabular row" style={{ gap: 8 }}>
                  {user.apartmentId}
                  <button
                    className="tap"
                    onClick={() => void copyId()}
                    aria-label={t('profile.copyId')}
                  >
                    {copied ? <Check size={16} color="green" /> : <Copy size={16} />}
                  </button>
                  {copied && (
                    <span className="muted" role="status">
                      {t('profile.copied')}
                    </span>
                  )}
                </dd>
                <dt>{t('auth.phone')}</dt>
                <dd className="tabular">{user.phone}</dd>
                <dt>{isL1 ? t('profile.accountType') : t('profile.designation')}</dt>
                <dd>
                  <Badge tone={isL1 ? 'success' : 'accent'}>
                    {TIER_DESIGNATION_MAP[user.tier] ?? user.tier}
                  </Badge>
                </dd>
                {/* Owners (L1) don't see who their sponsor is; executives (L2-L6) keep the row. */}
                {!isL1 && (
                  <>
                    <dt>{t('profile.sponsor')}</dt>
                    <dd>
                      {user.sponsor ? (
                        <>
                          {user.sponsor.fullName}
                          <span className="muted tabular"> ({user.sponsor.userId})</span>
                          <span className="muted tabular" data-testid="sponsor-phone"> · {user.sponsor.phoneMasked}</span>
                        </>
                      ) : user.parentId ? (
                        <>
                          {user.parentName ?? user.parentId}
                          {user.parentName && <span className="muted tabular"> ({user.parentId})</span>}
                        </>
                      ) : needsSponsor ? (
                        <span role="alert" data-testid="sponsor-missing" style={{ color: '#b45309', fontWeight: 600 }}>
                          {t('profile.noSponsorAdvisory')}
                        </span>
                      ) : (
                        t('profile.noSponsorDirector')
                      )}
                    </dd>
                  </>
                )}
              </dl>
            </Card>

            <KycDetailsCard user={user} />
            {/* Nominee: L1 only (owner decision) — the card also guards by tier */}
            {isL1 && <NomineeCard user={user} />}
            <LinkedPaymentCard payout={user.payout} />
            <ChangePasswordCard phone={user.phone} />
          </div>

          {/* 3. Security & Sign Out Strip */}
          <div className="profile-action-card">
            <div className="profile-action-left">
              <div className="profile-action-icon-wrap">
                <Lock size={18} />
              </div>
              <div>
                <strong style={{ fontSize: '0.84375rem', color: '#14251c', display: 'block' }}>
                  Bank-Grade KYC & Security Session
                </strong>
                <span className="muted" style={{ fontSize: '0.75rem' }}>
                  Authenticated with dual-token rotation and encrypted credentials
                </span>
              </div>
            </div>

            <Button
              variant="danger"
              data-testid="profile-logout"
              loading={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })}
            >
              <LogOut size={16} /> {t('nav.logout')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
